import { type HTMLAttributes, forwardRef } from 'react';

import { cn } from '../utils/cn';

export interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Spinner size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Spinner color variant
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary' | 'white';

  /**
   * Loading text to display
   */
  label?: string;
}

/**
 * Spinner component
 *
 * A loading spinner to indicate ongoing processes.
 *
 * @example
 * ```tsx
 * <Spinner />
 *
 * <Spinner size="lg" label="Loading..." />
 * ```
 */
export const Spinner = forwardRef<HTMLDivElement, SpinnerProps>(
  ({ size = 'md', variant = 'primary', label, className, ...props }, ref) => {
    const sizeStyles = {
      sm: 'h-4 w-4 border-2',
      md: 'h-8 w-8 border-2',
      lg: 'h-12 w-12 border-3',
      xl: 'h-16 w-16 border-4',
    };

    const variantStyles = {
      primary: 'border-primary-200 border-t-primary-600',
      secondary: 'border-secondary-200 border-t-secondary-600',
      white: 'border-white/30 border-t-white',
    };

    const labelSizeStyles = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
    };

    return (
      <div
        ref={ref}
        className={cn('flex flex-col items-center justify-center gap-3', className)}
        role="status"
        {...props}
      >
        <div
          className={cn('animate-spin rounded-full', sizeStyles[size], variantStyles[variant])}
        />
        {label && (
          <p className={cn('text-gray-600 dark:text-gray-400', labelSizeStyles[size])}>{label}</p>
        )}
        <span className="sr-only">{label || 'Loading...'}</span>
      </div>
    );
  }
);

Spinner.displayName = 'Spinner';
